import React, { FC, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";

import { Button } from "@natera/material/lib/button";

import { StepperContext } from "@natera/stepper";
import { UppAuthContext } from "@app/provider";
import { routes } from "@app/routing";
import LabItem from "./labItem";
import { StepperContent } from "@app/components/ui/layout";
import { Form } from "@natera/form";
import { LoadingContext } from "@natera/platform/lib/components/context";
import { MilesSelector } from "@app/components/labLocator";
import { ZipSelector } from "@app/components/forms";
import { useLabLocator } from "@app/hooks";
import { ZipValidationErrors } from "@app/components/forms";

import "./labList.scss";

const messages = defineMessages({
  sampleDrawLabTitle: {
    id: "labCollectionLabListTitle",
    defaultMessage: "Select a local lab near you",
  },
  labCollectionLabListDescription: {
    id: "labCollectionLabListDescription",
    defaultMessage:
      'Please call the lab or click "Schedule appointment" to make an appointment.',
  },
  labCollectionLabListDone: {
    id: "labCollectionLabListDone",
    defaultMessage: "Done",
  },
  labCollectionLabListBack: {
    id: "labCollectionLabListBack",
    defaultMessage: "Back To Search",
  },
  labCollectionLabListNoLabs: {
    id: "labCollectionLabListNoLabs",
    defaultMessage:
      "No labs found in this area. Try another ZIP code or distance.",
  },
  labCollectionLabListFetchFailed: {
    id: "labCollectionLabListFetchFailed",
    defaultMessage: "Unable to load labs at this time. Please try again later.",
  },
});

const LabList: FC = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const { profile } = useContext(UppAuthContext);
  const { goBack } = useContext(StepperContext);
  const {
    zipCode,
    setZipCode,
    distance,
    setDistance,
    sites: bloodDrawSites,
    isLoading,
    error,
  } = useLabLocator();

  const showFetchFailedFallback = error === ZipValidationErrors.FETCH_FAILED;
  const showNoLabsFallback =
    error === ZipValidationErrors.NO_LAB || showFetchFailedFallback;
  // The under-input hint is reserved for ZIP-format validation only
  // (INVALID_ZIP_CODE / EMPTY). NO_LAB (Puerto Rico or empty-result) and
  // FETCH_FAILED both render only in the big centered message slot below.
  const zipSelectorError =
    error === ZipValidationErrors.NO_LAB ||
    error === ZipValidationErrors.FETCH_FAILED
      ? null
      : error;

  const onZipChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    setZipCode(event.target.value);
  };

  const done: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    navigate(routes.home, { replace: true });
  };

  return (
    <div className="lab-list__container">
      <StepperContent
        title={intl.formatMessage(messages.sampleDrawLabTitle)}
        subTitle={intl.formatMessage(messages.labCollectionLabListDescription)}
      >
        <Form
          onSubmit={done}
          noValidate
          buttons={
            <>
              {profile && (
                <Button type="submit" unelevated data-testid="lab-list-done">
                  {intl.formatMessage(messages.labCollectionLabListDone)}
                </Button>
              )}
              <Button
                onClick={goBack}
                type="button"
                data-testid="lab-list-back-to-search"
              >
                {intl.formatMessage(messages.labCollectionLabListBack)}
              </Button>
            </>
          }
        >
          <div className="lab-list__container__search">
            <ZipSelector
              zipCode={zipCode}
              onChange={onZipChange}
              zipValidationErrorKey={zipSelectorError}
            />
            <MilesSelector value={distance} onSelect={setDistance} />
          </div>
          <LoadingContext isLoading={isLoading}>
            <div className="lab-list__container__list">
              {showNoLabsFallback ||
              !bloodDrawSites ||
              bloodDrawSites.length === 0 ? (
                <p
                  className="lab-list__container__empty"
                  data-testid="lab-list-empty"
                >
                  {intl.formatMessage(
                    showFetchFailedFallback
                      ? messages.labCollectionLabListFetchFailed
                      : messages.labCollectionLabListNoLabs,
                    { distance }
                  )}
                </p>
              ) : (
                bloodDrawSites.map((bloodDrawSite) => (
                  <LabItem
                    bloodDrawSite={bloodDrawSite}
                    key={bloodDrawSite.id}
                    showScheduleLink
                  />
                ))
              )}
            </div>
          </LoadingContext>
        </Form>
      </StepperContent>
    </div>
  );
};

export default LabList;
